/* eslint-disable max-lines -- we decided not to split the shell connector in multiple files */
import { inject, Inject, Injectable, Injector } from '@angular/core';
import { Store } from '@sama/app-state';
import { assert, isDefined } from '@sama/js-core';
import { bindKey } from '@sama/micro-dash';
import { cloneDeep } from 'lodash';
import { lastValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import {
  getShapeForEventTracking,
  ShapeState,
} from '../../../../cross-team-shared/annotation/shared/state/shape-state';
import { TrackingEvent } from '../../../../cross-team-shared/event-tracking/event-utils';
import { AuthorizationService } from '../../../../cross-team-shared/misc-services/authorization.service';
import { RetryableRequestResult } from '../../../../cross-team-shared/misc-services/resources/url-request.service';
import {
  isOrthographicVideoOutput,
  isPointCloudOutput,
} from '../../../../cross-team-shared/misc-utils/output-utils';
import { ToolName } from '../../../../cross-team-shared/tools-names';
import { outputAnnotationInfoStoreToken } from '../../../../outputs/annotation/annotation-shared/annotation-providers';
import { AbstractAnnotationStore } from '../../../../outputs/annotation/annotation-shared/state/root/abstract-annotation-store';
import { TagService } from '../../../../outputs/annotation/annotation-shared/utils/tag.service';
import { PointCloudStore } from '../../../../outputs/annotation/point-cloud/state/point-cloud-store';
import { VideoStore } from '../../../../outputs/annotation/video/state/video-store';
import { EventTrackingService } from '../../../../shared/event-tracking/event-tracking.service';
import { ShapeGroupOrderState } from '../../../../team-apis/content/content-connector';
import { InFlightExtrapolationRequest } from '../../../../team-apis/content/in-flight-extrapolation-request';
import { ViewBox } from '../../../../team-apis/content/view-box';
import { EventTrackingOptions } from '../../../../team-apis/core/event-tracking';
import { ShellConnector as ShellConnectorInterface } from '../../../../team-apis/core/shell-connector';
import {
  Point2D,
  Point3D,
  VectorTaggingShapeType,
} from '../../../../team-apis/otterdam/answers/shape-typings';
import { AnnotationOutput } from '../../../../team-apis/otterdam/outputs/annotation-output';
import { getPrimaryOutputId } from '../../../h3ml/state/fusion-state.utils';
import {
  OutputInfo,
  outputInfoStoreToken,
} from '../../../h3ml/state/output-info';
import { TaskWorkspaceStore } from '../../../h3ml/state/task-workspace-store';
import { CanvasContextMenuService } from '../../../shared/context-menu/canvas-context-menu.service';
import { ShapeContextMenuService } from '../../../shared/context-menu/shape-context-menu.service';
import { ClientFeedbackDialogService } from '../../../shared/feedback/client-feedback-dialog.service';
import { ShapeFeedbackDialogService } from '../../../shared/feedback/shape-feedback-dialog.service';
import { isKeyboardShortcutName } from '../../../shared/keyboard-shortcuts/keyboard-shortcuts';
import { RootKeyboardShortcutService } from '../../../shared/keyboard-shortcuts/root-keyboard-shortcut.service';
import { AnnotationCreateDeleteService } from '../../../shared/misc-services/annotation-create-delete.service';
import { FeatureFlagService } from '../../../shared/misc-services/feature-flag.service';
import { ObjectScopingService } from '../../../shared/misc-services/object-scoping.service';
import { ObjectlessShapeService } from '../../../shared/misc-services/objectless-shape.service';
import { ShapeService } from '../../../shared/misc-services/shape.service';
import { SmartLoaderService } from '../../../shared/misc-services/smart-loader.service';
import { FusionManager } from './fusion/fusion-manager';
import { answerHelper2D } from './shape-layer-answer-helper';
import { InteractionModeService } from './utils/interaction-mode.service';
import { PushAndTrackService } from './utils/push-and-track.service';
import { SelectionService } from './utils/selection.service';
import { ShapeSelectionService } from './utils/shape-selection.service';
import { ZoomPanelService } from './zoom-panel/zoom-panel.service';

@Injectable()
export class ShellConnector implements ShellConnectorInterface {
  claimNextInternalId = bindKey(this.taskWorkspaceStore, 'claimNextInternalId');
  hasFeatureFlag = bindKey(this.featureFlagService, 'hasFlag');
  hasPermission = bindKey(this.authorizationService, 'hasPermission');
  restrictInteractions = bindKey(
    this.interactionModeService,
    'restrictInteractions',
  );
  openShapeContextMenu = bindKey(inject(ShapeContextMenuService), 'open');
  openCanvasContextMenu = bindKey(inject(CanvasContextMenuService), 'open');
  trackEvent = bindKey(this.eventTrackingService, 'track');

  private outputAnnotationInfoStore = inject(outputAnnotationInfoStoreToken);
  private sensorRendererStateStore = this.outputAnnotationInfoStore(
    'sensorRendererState',
  );
  private annotationCreateDeleteService = inject(AnnotationCreateDeleteService);
  private objectScopingService = inject(ObjectScopingService);

  constructor(
    private eventTrackingService: EventTrackingService,
    private featureFlagService: FeatureFlagService,
    private fusionManager: FusionManager,
    private injector: Injector,
    private interactionModeService: InteractionModeService,
    private keyboardShortcutService: RootKeyboardShortcutService,
    private pushAndTrackService: PushAndTrackService,
    @Inject(outputInfoStoreToken)
    private outputInfoStore: Store<OutputInfo<AnnotationOutput>>,
    private selectionService: SelectionService,
    private shapeSelectionService: ShapeSelectionService,
    private shapeFeedbackDialogService: ShapeFeedbackDialogService,
    private smartLoaderService: SmartLoaderService,
    private store: AbstractAnnotationStore,
    private tagService: TagService,
    private taskWorkspaceStore: TaskWorkspaceStore,
    private shapeService: ShapeService,
    private objectlessShapeService: ObjectlessShapeService,
    private clientFeedbackDialogService: ClientFeedbackDialogService,
    private authorizationService: AuthorizationService,
  ) {}

  addMissingShapeFeedback(point: Point2D | Point3D) {
    this.selectionService.setSelection([], []);
    this.shapeFeedbackDialogService.openAddMissingShapeFeedbackDialog(point);
  }

  addClientFeedback(point: Point2D | Point3D) {
    this.clientFeedbackDialogService.openCreateFeedbackDialog(point);
  }

  createShape(shape: ShapeState, method?: string) {
    const { output } = this.outputInfoStore.state();
    if (!this.taskWorkspaceStore.isUsingObjects()) {
      this.createShapeObjectless(shape, method);
      return;
    }

    let shapeToCreate = cloneDeep(shape);

    if (isOrthographicVideoOutput(output)) {
      shapeToCreate = this.shapeService.groundShape(shapeToCreate, output.id);
    }

    const shapeCreated = this.annotationCreateDeleteService.createShape(
      shapeToCreate,
      output.name,
    );

    assert(shapeCreated, 'shape creation should be prevented if not allowed');
    this.selectionService.setShapeSelection([shapeCreated.index]);
    this.pushAndTrack({
      id: 'create_shape',
      bucket: 'annotation',
      shapes: [getShapeForEventTracking(shapeCreated)],
      method,
    });
  }

  createShapeObjectless(shape: ShapeState, method?: string) {
    const { output } = this.outputInfoStore.state();

    let shapeToCreate = cloneDeep(shape);

    shapeToCreate = this.objectlessShapeService.createShape(
      shapeToCreate,
      output.name,
      this.featureFlagService.hasFlag('select_first_tag'),
    );

    this.shapeSelectionService.setSelection([shapeToCreate.index]);
    this.tagService.openTagEditor(shapeToCreate.index, 'create_shape');
    this.pushAndTrack({
      id: 'create_shape',
      bucket: 'annotation',
      shapes: [getShapeForEventTracking(shapeToCreate)],
      method,
    });
  }

  convertShapeStateToAnswer(shapeState: ShapeState) {
    assert(
      !isPointCloudOutput(this.outputInfoStore.state().output),
      'This function is intended for 2D MLE only',
    );

    return answerHelper2D.shapeStateExternal(
      shapeState as ShapeState<Point2D, VectorTaggingShapeType>,
      [],
    );
  }

  modifyShapeSelection(
    indexes: number[],
    setOrToggle?: 'set' | 'toggle',
    eventTrackingValue?: { source: string },
  ) {
    if (setOrToggle === 'toggle') {
      this.selectionService.toggleShapeSelection(indexes, eventTrackingValue);
    } else {
      this.selectionService.setShapeSelection(indexes, eventTrackingValue);
    }
  }

  onAssetLoadError(reason: string) {
    this.smartLoaderService.cancel(
      getPrimaryOutputId(this.outputInfoStore.state().output),
    );
    this.store.assign({ status: 'error', errorMessage: reason });
  }

  setMousePosition(mousePosition: {
    clientPoint: Point2D;
    imagePoint: Point2D;
  }) {
    // Content awaits rendering (using a delay(0)) before calculating the mouse
    // position and calling this method. While it awaits, the store can be
    // deleted due to task submission.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- state can be undefined when deleted
    if (!this.store.state()) {
      return;
    }
    this.store('mousePosition').set(mousePosition);
  }

  setZoomPanelSettings(scale: number, enabled?: boolean) {
    this.store('zoomPanel')('scale').set(scale);
    isDefined(enabled) && this.store('zoomPanel')('enabled').set(enabled);
  }

  scopeObject(shapeId: number) {
    const shape = this.shapeService.getShapeStore(shapeId).state();

    this.objectScopingService.scopeObjectContainingShape(shape);
    this.selectionService.setShapeSelection([shapeId]);
  }

  openZoomPanel() {
    // injecting this here instead of in the constructor to avoid a cyclic
    // dependency error because of the feature flag service in the
    // ZoomPanelService
    const zoomPanelService = this.injector.get(ZoomPanelService);
    zoomPanelService.openZoomPanel(false, 'tool');
  }

  toggleZoomPanel() {
    // injecting this here instead of in the constructor to avoid a cyclic
    // dependency error because of the feature flag service in the
    // ZoomPanelService
    const zoomPanelService = this.injector.get(ZoomPanelService);
    zoomPanelService.toggleZoomPanel('button');
  }

  propagateViewBoxUpdate(outputName: string, viewBox: ViewBox) {
    this.taskWorkspaceStore('viewBoxes')(outputName).set(viewBox);
  }

  propagateViewBoxPaddingUpdate(viewBoxPadding?: number) {
    this.taskWorkspaceStore('viewBoxPadding').set(viewBoxPadding);
  }

  pushAndTrack(event: TrackingEvent, options?: EventTrackingOptions) {
    this.pushAndTrackService.pushAndTrack(event, options);
  }

  setSelectedTool(name: ToolName, mode?: string) {
    this.outputAnnotationInfoStore('selectedTool').set({ name, mode });
  }

  setShapes(shapeGroupOrderState: ShapeGroupOrderState) {
    const { output } = this.outputInfoStore.state();
    if (!this.taskWorkspaceStore.isUsingObjects()) {
      this.objectlessShapeService.updateShapes(shapeGroupOrderState, output);
    } else {
      this.shapeService.updateKeyLocationsAndSource(
        shapeGroupOrderState,
        output,
      );
    }
  }

  handleCanvasClick() {
    this.taskWorkspaceStore('sidePanel')('selectedTabName').set('annotation');
  }

  getHumanReadableShortcut(shortcutName: string) {
    if (shortcutName === 'platform') {
      return this.keyboardShortcutService.humanReadableModifier('platform');
    } else if (!isKeyboardShortcutName(shortcutName)) {
      return undefined;
    }
    return this.keyboardShortcutService.humanReadableShortcut(shortcutName);
  }

  getMlAssistRange() {
    assert(
      this.store instanceof VideoStore,
      'Should only be called in video outputs',
    );
    return this.store.state().mlAssistRange;
  }

  notifySideViewRendererIsAllowed(isAllowed: boolean) {
    assert(
      this.store instanceof PointCloudStore,
      'Should only be called in point cloud outputs',
    );

    this.store('sideViewsIsAllowed').set(isAllowed);
  }

  queueAssetRequest(
    frameNumber: number,
    sendRequest: () => Promise<RetryableRequestResult>,
  ) {
    return this.smartLoaderService.queueRequest(
      getPrimaryOutputId(this.fusionManager.output),
      frameNumber,
      sendRequest,
    );
  }

  setFrameLoaded(frameNumber: number) {
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (this.sensorRendererStateStore.state()) {
      this.setFrameAsLoaded(frameNumber);
    } else {
      // This method can sometimes be called before the sensor renderer
      // state has initialized. When that happens, we wait for the state to be
      // ready before proceeding.
      void lastValueFrom(
        this.sensorRendererStateStore.$.pipe(filter(isDefined), take(1)),
      ).then(() => {
        this.setFrameAsLoaded(frameNumber);
      });
    }
  }

  updateInFlightMlExtrapolationRequests(
    mlRequests: InFlightExtrapolationRequest[],
  ) {
    this.store('inFlightMlExtrapolationRequests').set(mlRequests);
  }

  private setFrameAsLoaded(frameNumber: number) {
    this.sensorRendererStateStore('loadedFrames')(frameNumber).set(true);
  }
}
